import { useState } from "react";
import { abs } from "@/lib/site";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { TextGenerator, type GeneratorOutput } from "@/components/TextGenerator";
import {
  COMBINING_STYLES,
  UTILITY_TOOLS,
  applyUpsideDown,
  applyCombining,
  applyZalgo,
  type CombiningStyle,
  type ZalgoIntensity,
} from "@/lib/text-generators";
import { FANCY_STYLE_COUNT } from "@/data/generator-stats.generated";

interface ToolPage {
  h1: string;
  title: string;
  description: string;
  lede: string;
  how: string;
  placeholder: string;
  sample: string;
}

const TOOL_PAGES: Record<string, ToolPage> = {
  "upside-down": {
    h1: "Upside-down text generator",
    title: "Upside-down text generator — flip your text ¡ʇxǝʇ",
    description:
      "Flip text upside down using rotated Unicode glyphs. Your message reads bottom-to-top and reversed, ready to paste into bios, comments and messages. Live preview, one-click copy, real characters only.",
    lede: "Write something and watch it flip over. Each letter is swapped for a real Unicode character that looks like its upside-down twin, then the whole line is reversed so it reads correctly when rotated 180°.",
    how: "Rotating text relies on lucky lookalikes already in Unicode: a turned 'e' (ǝ), a small-capital rotated 'A' (∀), an upside-down '?' (¿), and so on. We map each character to its closest real rotated counterpart and reverse the order of the line — never inventing a glyph. Letters without a convincing rotated form fall back to themselves, so nothing turns into a missing-character box.",
    placeholder: "Type to flip it upside down…",
    sample: "Flip This Text",
  },
  strikethrough: {
    h1: "Strikethrough & underline generator",
    title: "Strikethrough text generator — cross out & underline anywhere",
    description:
      "Add a strikethrough, underline or slash through any text using Unicode combining marks — no rich-text editor needed. Works in plain-text fields like bios, chats and spreadsheets. Live preview and one-click copy.",
    lede: "Cross words out or underline them even where there are no formatting buttons. Type your text and copy the struck-through, underlined or slashed version — it keeps its decoration wherever plain text is allowed.",
    how: "Each visible character is followed by a combining mark — a long stroke overlay for strikethrough (U+0336), a low line for underline (U+0332), or a long solidus for the slash. Combining marks attach to the character before them, so the line stays a single run of plain text. That means it pastes into places a normal underline button could never reach, like a Twitter bio or a chat message.",
    placeholder: "Type to cross it out…",
    sample: "Crossed Out",
  },
  zalgo: {
    h1: "Zalgo / glitch text generator",
    title: "Zalgo text generator — cursed, glitchy text with intensity control",
    description:
      "Generate zalgo 'cursed' text by stacking Unicode combining diacritics above, through and below your letters. Choose mild, medium or intense, preview live, and copy with one click. Deterministic output, real characters only.",
    lede: "Drench your text in chaos. Zalgo stacks combining diacritical marks onto each letter so it appears to drip, glitch and bleed into the lines around it. Pick how far it goes and copy the result.",
    how: "Every character is decorated with a random handful of real combining diacritics drawn from the U+0300–U+036F range — marks that sit above, through and below the base letter. The intensity control just changes how many marks each letter gets. The randomness is seeded from your text, so the same input always produces the same glitch and the preview never jitters as you interact with it.",
    placeholder: "Type to summon the glitch…",
    sample: "Cursed Text",
  },
};

export const Route = createFileRoute("/font-generator/$slug")({
  loader: ({ params }) => {
    const page = TOOL_PAGES[params.slug];
    if (!page) throw notFound();
    return { slug: params.slug, page };
  },
  head: ({ params }) => {
    const page = TOOL_PAGES[params.slug];
    if (!page) return {};
    const url = `/font-generator/${params.slug}`;
    return {
      meta: [
        { title: page.title },
        { name: "description", content: page.description },
        { property: "og:title", content: page.h1 },
        { property: "og:description", content: page.lede },
        { property: "og:url", content: abs(url) },
      ],
      links: [{ rel: "canonical", href: abs(url) }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: page.h1,
            applicationCategory: "UtilityApplication",
            operatingSystem: "Any",
            url: abs(url),
            offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          }),
        },
      ],
    };
  },
  component: ToolPageView,
});

const COMBINING_LABELS: Record<CombiningStyle, string> = {
  strikethrough: "Strikethrough",
  underline: "Underline",
  "slash-through": "Slash through",
};

function ToolPageView() {
  const { slug, page } = Route.useLoaderData();

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-12 sm:px-6 sm:py-16">
        <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground">
          <Link to="/font-generator" className="hover:text-foreground">
            Font generator
          </Link>
          <span aria-hidden="true"> / </span>
          <span className="text-foreground/80">{page.h1}</span>
        </nav>

        <h1 className="mt-3 font-display text-4xl font-bold sm:text-5xl">{page.h1}</h1>
        <p className="mt-4 text-lg text-foreground/85">{page.lede}</p>

        <GeneratorBody slug={slug} placeholder={page.placeholder} sample={page.sample} />

        <section aria-labelledby="how-heading" className="mt-12">
          <h2 id="how-heading" className="font-display text-xl font-semibold">
            How it works
          </h2>
          <p className="mt-3 text-foreground/85">{page.how}</p>
        </section>

        <nav className="mt-12 border-t border-border pt-6">
          <p className="text-sm text-muted-foreground">More tools</p>
          <ul className="mt-3 flex flex-wrap gap-2">
            <li>
              <Link
                to="/font-generator"
                className="inline-flex rounded-md border border-border bg-surface/60 px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
              >
                Font generator ({FANCY_STYLE_COUNT})
              </Link>
            </li>
            {UTILITY_TOOLS.filter((t) => t.slug !== slug).map((t) => (
              <li key={t.slug}>
                <Link
                  to="/font-generator/$slug"
                  params={{ slug: t.slug }}
                  className="inline-flex rounded-md border border-border bg-surface/60 px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
                >
                  {t.name}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </main>
      <SiteFooter />
    </div>
  );
}

function GeneratorBody({ slug, placeholder, sample }: { slug: string; placeholder: string; sample: string }) {
  if (slug === "upside-down") {
    return (
      <TextGenerator
        defaultValue={sample}
        placeholder={placeholder}
        transform={(input) => [{ id: "upside-down", label: "Upside-down", value: applyUpsideDown(input) }]}
      />
    );
  }

  if (slug === "strikethrough") {
    return (
      <TextGenerator
        defaultValue={sample}
        placeholder={placeholder}
        transform={(input) =>
          COMBINING_STYLES.map((st) => ({ id: st, label: COMBINING_LABELS[st], value: applyCombining(st, input) }))
        }
      />
    );
  }

  if (slug === "zalgo") {
    return <ZalgoGenerator placeholder={placeholder} sample={sample} />;
  }

  return null;
}

const ZALGO_LEVELS: { id: ZalgoIntensity; label: string }[] = [
  { id: "mild", label: "Mild" },
  { id: "medium", label: "Medium" },
  { id: "intense", label: "Intense" },
];

function ZalgoGenerator({ placeholder, sample }: { placeholder: string; sample: string }) {
  const [intensity, setIntensity] = useState<ZalgoIntensity>("medium");

  const controls = (
    <fieldset>
      <legend className="text-sm font-medium text-foreground/90">Intensity</legend>
      <div className="mt-2 inline-flex rounded-lg border border-border bg-surface/60 p-1">
        {ZALGO_LEVELS.map((lvl) => (
          <button
            key={lvl.id}
            type="button"
            onClick={() => setIntensity(lvl.id)}
            aria-pressed={intensity === lvl.id}
            className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
              intensity === lvl.id ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {lvl.label}
          </button>
        ))}
      </div>
    </fieldset>
  );

  const transform = (input: string): GeneratorOutput[] => [
    { id: `zalgo-${intensity}`, label: `Zalgo — ${intensity}`, value: applyZalgo(input, intensity) },
  ];

  return <TextGenerator defaultValue={sample} placeholder={placeholder} transform={transform} controls={controls} />;
}
